import React from 'react';
import { motion } from 'framer-motion';
import { Facebook, Instagram, Linkedin, LifeBuoy, Mail, MapPin, Twitter } from 'lucide-react';
import {
  CardGrid,
  ContactForm,
  Container,
  FAQ,
  FeatureCard,
  IconChip,
  Reveal,
  Section,
  SectionHeader,
  TextLink,
} from '../components/page';
import { Glow } from '../components/ui';
import { fadeUp } from '../components/motion';
import SmartLink from '../components/SmartLink';

const SOCIALS = [
  { icon: Twitter, label: 'Twitter', href: 'https://twitter.com' },
  { icon: Instagram, label: 'Instagram', href: 'https://instagram.com' },
  { icon: Facebook, label: 'Facebook', href: 'https://facebook.com' },
  { icon: Linkedin, label: 'LinkedIn', href: 'https://linkedin.com' },
];

const SUPPORT_TIERS = [
  'Free users: Basic support (guides, 1 chat/month)',
  'Basic users: Full live help support',
  'Max users: Priority support with dedicated agent',
];

const FAQS = [
  {
    q: 'What are your support hours?',
    a: 'Our email support team is available 24/7, with responses typically within 24-48 hours. Live chat support is available Monday through Friday, 9 AM to 6 PM EST.',
  },
  {
    q: "I'm having trouble with my account. What should I do?",
    a: 'For account-related issues, the fastest solution is to use our in-app Resolution Center. Go to your profile and select "Resolution Center" for guided troubleshooting and support options.',
  },
  {
    q: 'How quickly will I receive a response?',
    a: 'Email responses are typically within 24-48 hours. Max plan users receive priority support with responses within 12 hours. In-app chat support typically connects you with an agent within minutes during business hours.',
  },
  {
    q: 'Can I schedule a call with support?',
    a: 'Max plan users can schedule one-on-one support calls with our team. In the app, go to Resolution Center → Schedule Call to book a time that works for you. Basic and Free users can upgrade their plan to access this feature.',
  },
];

const SocialButtons = () => (
  <div className="flex flex-wrap gap-2">
    {SOCIALS.map(({ icon: Icon, label, href }) => (
      <SmartLink
        key={label}
        to={href}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-white/[0.08] px-4 text-sm font-medium text-slate-300 transition-colors duration-200 hover:border-white/20 hover:text-white"
      >
        <Icon className="h-4 w-4" />
        {label}
      </SmartLink>
    ))}
  </div>
);

const ContactHero = () => (
  <section className="relative overflow-hidden pb-16 pt-40 sm:pb-24 sm:pt-48">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute inset-0 bg-grid-fade bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />
      <Glow className="left-1/2 top-[-14rem] h-[32rem] w-[56rem] -translate-x-1/2 bg-brand-500/20" />
    </div>
    <Container className="relative">
      <motion.div
        initial="hidden"
        animate="show"
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
        className="mx-auto max-w-4xl text-center"
      >
        <motion.span variants={fadeUp} className="eyebrow">
          Reach Out
        </motion.span>
        <motion.h1
          variants={fadeUp}
          className="mt-6 text-5xl font-extrabold leading-[1.02] tracking-tightest sm:text-6xl lg:text-7xl"
        >
          <span className="text-gradient pr-[0.06em]">We're Here to </span>
          <span className="text-gradient-brand pr-[0.06em]">Help!</span>
        </motion.h1>
        <motion.p
          variants={fadeUp}
          className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-slate-400 sm:text-xl sm:leading-relaxed"
        >
          At Groupmoney, we're committed to making your group financial experience as smooth and enjoyable as possible. Whether you have a question, need help with a group issue, or just want to share feedback, we'd love to hear from you. Our team is ready to assist—let's keep your group thriving together!
        </motion.p>
      </motion.div>
    </Container>
  </section>
);

const ContactPage = () => (
  <>
    <ContactHero />

    {/* Ways to reach us */}
    <Section className="!pt-0">
      <Container>
        <SectionHeader eyebrow="Get in Touch" title="Here's How You Can Reach Us" />
        <CardGrid cols={3} className="mt-16 sm:mt-20">
          <FeatureCard
            icon={Mail}
            title="Email Us"
            footer={<TextLink to="mailto:support@groupmoney.app">Send an email</TextLink>}
          >
            <p>
              Drop us a line at{' '}
              <a href="mailto:support@groupmoney.app" className="font-medium text-brand-300 hover:text-white">
                support@groupmoney.app
              </a>
              . We'll respond within 24-48 hours.
            </p>
          </FeatureCard>

          <FeatureCard
            icon={MapPin}
            accent="violet"
            title="Visit Us"
            footer={<TextLink to="https://maps.google.com">View on map</TextLink>}
          >
            <p className="text-slate-300">
              Groupmoney
              <br />
              3-425 McArthur Ave.
              <br />
              Ottawa, ON, Canada
            </p>
            <p className="mt-4 text-sm">
              <span className="font-semibold text-slate-300">Note:</span> We're primarily a digital platform, so email or in-app support is the fastest way to get help!
            </p>
          </FeatureCard>

          <FeatureCard
            icon={LifeBuoy}
            accent="emerald"
            title="In-App Support"
            footer={<TextLink to="/resolution-center">Open Resolution Center</TextLink>}
          >
            <p>Log in to your account, go to your profile, and visit the "Resolution Center" for quick assistance.</p>
            <ul className="mt-5 space-y-2">
              {SUPPORT_TIERS.map((tier) => {
                const [label, text] = tier.split(': ');
                return (
                  <li key={tier} className="rounded-xl border border-white/[0.06] bg-ink-900/80 px-3.5 py-2.5 text-sm">
                    <span className="font-semibold text-white">{label}:</span> {text}
                  </li>
                );
              })}
            </ul>
          </FeatureCard>
        </CardGrid>
      </Container>
    </Section>

    {/* Message form */}
    <Section className="overflow-hidden">
      <Glow className="right-[-12rem] top-1/4 h-[28rem] w-[28rem] bg-violet-600/10" />
      <Container className="relative">
        <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
          <div className="lg:col-span-2">
            <SectionHeader
              align="left"
              eyebrow="Send a Message"
              title="Let's Start a Conversation"
              description="Have a specific question or feedback? Use the form to send us a direct message and we'll get back to you as soon as possible."
            />
            <Reveal delay={0.15} className="surface mt-10 p-6 sm:p-8">
              <h3 className="text-lg font-bold tracking-tight text-white">Follow Us</h3>
              <p className="mt-2 text-slate-400">Stay updated with our latest news and connect with us on social media.</p>
              <div className="mt-5">
                <SocialButtons />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-3">
            <ContactForm
              subjects={['General Inquiry', 'Technical Support', 'Billing Question', 'Partnership Opportunity', 'Feedback']}
              consent={
                <span>
                  I agree to the{' '}
                  <SmartLink to="/privacy" className="font-medium text-brand-300 hover:text-white">
                    Privacy Policy
                  </SmartLink>{' '}
                  and consent to being contacted by email.
                </span>
              }
            />
          </Reveal>
        </div>
      </Container>
    </Section>

    {/* FAQ */}
    <Section id="faq" className="scroll-mt-24">
      <Container>
        <SectionHeader
          eyebrow="Frequently Asked Questions"
          title="Common Support Questions"
          description="Find quick answers to common questions about our support options and contact methods."
        />
        <FAQ items={FAQS} className="mt-16 sm:mt-20" />
      </Container>
    </Section>

    {/* HQ */}
    <Section className="!pt-0">
      <Container>
        <Reveal>
          <div className="surface relative overflow-hidden">
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-grid-fade bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)]"
            />
            <Glow className="left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 bg-brand-500/20" />
            <div className="relative flex min-h-[18rem] flex-col items-center justify-center px-6 py-16 text-center">
              <span className="relative flex h-14 w-14 items-center justify-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400/20 motion-reduce:hidden" />
                <IconChip icon={MapPin} size="lg" className="relative !rounded-full" />
              </span>
              <p className="mt-6 text-2xl font-bold tracking-tight text-white">Groupmoney HQ</p>
              <p className="mt-1 text-slate-400">Ottawa, ON, Canada</p>
              <p className="mt-4 text-sm text-slate-500">Interactive map available on our website</p>
              <TextLink to="https://maps.google.com" className="mt-6">
                View on map
              </TextLink>
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>
  </>
);

export default ContactPage;
